import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Grid,
  TextField,
  Typography,
  Divider,
  Alert,
  IconButton,
  Switch,
  FormControlLabel,
  CircularProgress,
  Tabs,
  Tab
} from '@mui/material';
import {
  Close as CloseIcon,
  Person as PersonIcon,
  Home as HomeIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Delete as DeleteIcon,
  Work as WorkIcon
} from '@mui/icons-material';
import CustomerOverviewPanel from './CustomerOverviewPanel';

const CustomerDetailsDialog = ({ open, onClose, customer, onUpdate, onScheduleJob, refreshKey = 0 }) => {
  const [editMode, setEditMode] = useState(false);
  const [loading, setLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [tabValue, setTabValue] = useState(0);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    isActive: true
  });

  const apiBase = process.env.REACT_APP_API_URL?.replace(/\/$/, '') || '';

  useEffect(() => {
    if (customer) {
      setFormData({
        firstName: customer.firstName || '',
        lastName: customer.lastName || '',
        email: customer.email || '',
        phoneNumber: customer.phoneNumber || '',
        address: customer.address || '',
        city: customer.city || '',
        state: customer.state || '',
        zipCode: customer.zipCode || '',
        isActive: customer.isActive !== undefined ? customer.isActive : true
      });
      setEditMode(false);
      setError('');
      setSuccess('');
      setTabValue(0);

    }
  }, [customer]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleToggleActive = (e) => {
    setFormData(prev => ({
      ...prev,
      isActive: e.target.checked
    }));
  };

  const handleSave = async () => {
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      // Update user basic info
      const userResponse = await fetch(`${apiBase}/api/users/${customer.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          phoneNumber: formData.phoneNumber,
          isActive: formData.isActive
        })
      });

      if (!userResponse.ok) {
        const errorData = await userResponse.json();
        throw new Error(errorData.error || 'Failed to update user');
      }

      // Update client profile (address info)
      const profileResponse = await fetch(`${apiBase}/api/users/client-profile`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          address: formData.address,
          city: formData.city,
          state: formData.state,
          zipCode: formData.zipCode
        })
      });

      if (!profileResponse.ok) {
        const errorData = await profileResponse.json();
        throw new Error(errorData.error || 'Failed to update profile');
      }

      setSuccess('Customer updated successfully');
      setEditMode(false);

      // Notify parent component to refresh data
      if (onUpdate) {
        onUpdate();
      }

      // Auto-close success message after 2 seconds
      setTimeout(() => {
        setSuccess('');
      }, 2000);

    } catch (err) {
      setError(err.message || 'Failed to update customer');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    // Reset form to original customer data
    if (customer) {
      setFormData({
        firstName: customer.firstName || '',
        lastName: customer.lastName || '',
        email: customer.email || '',
        phoneNumber: customer.phoneNumber || '',
        address: customer.address || '',
        city: customer.city || '',
        state: customer.state || '',
        zipCode: customer.zipCode || '',
        isActive: customer.isActive !== undefined ? customer.isActive : true
      });
    }
    setEditMode(false);
    setError('');
    setSuccess('');
  };

  const handleDelete = async () => {
    const customerName = `${formData.firstName} ${formData.lastName}`;

    if (!window.confirm(
      `Are you sure you want to delete customer "${customerName}"?\n\n` +
      `This will permanently delete:\n` +
      `- The user account\n` +
      `- Customer profile\n` +
      `- All associated data (bookings, jobs, etc.)\n\n` +
      `This action cannot be undone!`
    )) {
      return;
    }

    setDeleteLoading(true);
    setError('');
    setSuccess('');

    try {
      const response = await fetch(`${apiBase}/api/users/delete/${customer.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to delete customer');
      }

      const data = await response.json();
      setSuccess(data.message || `Customer "${customerName}" deleted successfully`);

      // Notify parent component to refresh data and close dialog
      if (onUpdate) {
        onUpdate();
      }

      // Close dialog after a brief delay
      setTimeout(() => {
        onClose();
      }, 1500);

    } catch (err) {
      setError(err.message || 'Failed to delete customer');
    } finally {
      setDeleteLoading(false);
    }
  };

  if (!customer) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={1}>
            <PersonIcon color="primary" />
            <Typography variant="h6">
              Customer Details
            </Typography>
          </Box>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)}>
          <Tab label="Profile" icon={<PersonIcon />} iconPosition="start" />
          <Tab label="Overview & Jobs" icon={<WorkIcon />} iconPosition="start" />
        </Tabs>
      </Box>

      <DialogContent dividers>
        {/* Messages */}
        {error && (
          <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" onClose={() => setSuccess('')} sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}

        {/* Tab Panel 0: Profile Information */}
        {tabValue === 0 && (
          <Grid container spacing={3}>
            {/* Personal Information */}
          <Grid item xs={12}>
            <Box display="flex" alignItems="center" gap={1} mb={2}>
              <PersonIcon color="action" />
              <Typography variant="subtitle1" fontWeight="bold">
                Personal Information
              </Typography>
            </Box>
            <Divider sx={{ mb: 2 }} />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="First Name"
              name="firstName"
              value={formData.firstName}
              onChange={handleChange}
              disabled={!editMode || loading}
              variant={editMode ? "outlined" : "filled"}
              InputProps={{ readOnly: !editMode }}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Last Name"
              name="lastName"
              value={formData.lastName}
              onChange={handleChange}
              disabled={!editMode || loading}
              variant={editMode ? "outlined" : "filled"}
              InputProps={{ readOnly: !editMode }}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Email"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              disabled={!editMode || loading}
              variant={editMode ? "outlined" : "filled"}
              InputProps={{ readOnly: !editMode }}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Phone Number"
              name="phoneNumber"
              value={formData.phoneNumber}
              onChange={handleChange}
              disabled={!editMode || loading}
              variant={editMode ? "outlined" : "filled"}
              InputProps={{ readOnly: !editMode }}
            />
          </Grid>

          {/* Address Information */}
          <Grid item xs={12}>
            <Box display="flex" alignItems="center" gap={1} mb={2} mt={2}>
              <HomeIcon color="action" />
              <Typography variant="subtitle1" fontWeight="bold">
                Address Information
              </Typography>
            </Box>
            <Divider sx={{ mb: 2 }} />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Address"
              name="address"
              value={formData.address}
              onChange={handleChange}
              disabled={!editMode || loading}
              variant={editMode ? "outlined" : "filled"}
              InputProps={{ readOnly: !editMode }}
            />
          </Grid>

          <Grid item xs={12} sm={5}>
            <TextField
              fullWidth
              label="City"
              name="city"
              value={formData.city}
              onChange={handleChange}
              disabled={!editMode || loading}
              variant={editMode ? "outlined" : "filled"}
              InputProps={{ readOnly: !editMode }}
            />
          </Grid>

          <Grid item xs={12} sm={3}>
            <TextField
              fullWidth
              label="State"
              name="state"
              value={formData.state}
              onChange={handleChange}
              disabled={!editMode || loading}
              variant={editMode ? "outlined" : "filled"}
              InputProps={{ readOnly: !editMode }}
            />
          </Grid>

          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              label="Zip Code"
              name="zipCode"
              value={formData.zipCode}
              onChange={handleChange}
              disabled={!editMode || loading}
              variant={editMode ? "outlined" : "filled"}
              InputProps={{ readOnly: !editMode }}
            />
          </Grid>

          {/* Account Status */}
          <Grid item xs={12}>
            <Divider sx={{ my: 2 }} />
            <FormControlLabel
              control={
                <Switch
                  checked={formData.isActive}
                  onChange={handleToggleActive}
                  disabled={!editMode || loading}
                />
              }
              label={
                <Typography variant="body2">
                  Active Account {formData.isActive ? '(Enabled)' : '(Disabled)'}
                </Typography>
              }
            />
          </Grid>

          {/* Account Metadata */}
          <Grid item xs={12}>
            <Box sx={{ bgcolor: 'grey.50', p: 2, borderRadius: 1 }}>
              <Typography variant="caption" color="text.secondary" display="block">
                Customer ID: {customer.id}
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block">
                Created: {customer.createdAt ? new Date(customer.createdAt).toLocaleDateString() : 'N/A'}
              </Typography>
            </Box>
          </Grid>
        </Grid>
        )}

        {/* Tab Panel 1: Overview & Jobs */}
        {tabValue === 1 && customer?.id && (
          <CustomerOverviewPanel
            customerId={customer.id}
            onScheduleJob={onScheduleJob ? () => onScheduleJob(customer) : undefined}
            refreshKey={refreshKey}
          />
        )}
      </DialogContent>

      <DialogActions sx={{ justifyContent: 'space-between', px: 3, py: 2 }}>
        {/* Delete button on the left */}
        <Button
          variant="outlined"
          color="error"
          startIcon={deleteLoading ? <CircularProgress size={20} /> : <DeleteIcon />}
          onClick={handleDelete}
          disabled={loading || deleteLoading || editMode}
        >
          {deleteLoading ? 'Deleting...' : 'Delete Customer'}
        </Button>

        {/* Action buttons on the right */}
        <Box display="flex" gap={1}>
          {!editMode ? (
            <>
              <Button onClick={onClose}>Close</Button>
              <Button
                variant="contained"
                startIcon={<EditIcon />}
                onClick={() => setEditMode(true)}
              >
                Edit
              </Button>
            </>
          ) : (
            <>
              <Button
                onClick={handleCancel}
                startIcon={<CancelIcon />}
                disabled={loading}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                startIcon={loading ? <CircularProgress size={20} /> : <SaveIcon />}
                onClick={handleSave}
                disabled={loading}
              >
                {loading ? 'Saving...' : 'Save Changes'}
              </Button>
            </>
          )}
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default CustomerDetailsDialog;
